import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, RefreshCw, Download } from "lucide-react";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const STATUS_COLORS = {
  new: "bg-[#C62828] text-white",
  contacted: "bg-yellow-500 text-white",
  quoted: "bg-blue-600 text-white",
  won: "bg-green-700 text-white",
  lost: "bg-slate-500 text-white",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads-stats")]);
      setLeads(l.data); setStats(s.data);
    } catch (e) {
      console.error("Failed to load leads:", e);
      toast.error("Failed to load leads");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}/status`, { status });
      toast.success(`Status → ${status.toUpperCase()}`);
      setLeads((rows) => rows.map((r) => r.id === id ? { ...r, status } : r));
      if (selected?.id === id) setSelected({ ...selected, status });
      // refresh stats
      api.get("/leads-stats").then(({data}) => setStats(data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const rerouteLead = async (id) => {
    try {
      await api.post(`/leads/${id}/reroute`);
      toast.success("Re-routing — refresh in a few seconds.");
      // Re-fetch this lead after a short delay
      setTimeout(async () => {
        try {
          const { data } = await api.get(`/leads/${id}`);
          setLeads((rows) => rows.map((r) => r.id === id ? data : r));
          if (selected?.id === id) setSelected(data);
        } catch (err) { console.error("Failed to refetch lead:", err); }
      }, 1500);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/leads/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      const stamp = new Date().toISOString().slice(0,19).replace(/[:T]/g, "").slice(0,15);
      link.href = url;
      link.download = `lumberloop_leads_${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${leads.length} leads`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (filter !== "all" && l.status !== filter) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return [l.contractor_name, l.company, l.email, l.phone, l.zip_code, l.product_type, l.specifications]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [leads, query, filter]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="admin-leads-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tag">Admin · Lead Tracker</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-1 leading-none text-[#1A365D]">Leads.</h1>
          <p className="mt-2 text-slate-600">All contractor submissions in one place. Update status as you work each opportunity.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportCsv} className="btn-secondary flex items-center gap-2" data-testid="export-csv-btn"><Download size={16}/> Export CSV</button>
          <button onClick={load} className="btn-secondary flex items-center gap-2" data-testid="refresh-leads-btn"><RefreshCw size={16}/> Refresh</button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatBox label="Total" value={stats.total} accent/>
        {STATUSES.map((s) => (
          <StatBox key={s} label={s} value={stats.by_status?.[s] || 0}/>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16}/>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, ZIP, product…" className="ll-input pl-9" data-testid="lead-search-input"/>
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="ll-select max-w-[200px]" data-testid="lead-filter-select">
          <option value="all">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="mt-6 brutal-card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1A365D] text-white uppercase font-mono-ll text-[11px] tracking-widest">
            <tr>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Product</th>
              <th className="px-4 py-3 text-left">Contractor</th>
              <th className="px-4 py-3 text-left">Location</th>
              <th className="px-4 py-3 text-left">Routed</th>
              <th className="px-4 py-3 text-left">Submitted</th>
              <th className="px-4 py-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center font-mono-ll tracking-widest text-slate-500">LOADING…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center font-mono-ll tracking-widest text-slate-500">NO LEADS FOUND</td></tr>
            ) : filtered.map((l) => (
              <tr key={l.id} className="border-t-2 border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3"><span className={`px-2 py-1 text-[10px] font-mono-ll tracking-widest uppercase ${STATUS_COLORS[l.status] || "bg-slate-300"}`}>{l.status}</span></td>
                <td className="px-4 py-3 font-semibold text-[#1A365D]">{l.product_type}<div className="text-xs text-slate-500 font-normal">{l.quantity}</div></td>
                <td className="px-4 py-3">{l.contractor_name}<div className="text-xs text-slate-500">{l.email}</div></td>
                <td className="px-4 py-3 text-xs">
                  {[l.city, l.state].filter(Boolean).join(", ") || "—"}
                  <div className="font-mono-ll text-slate-500">{l.zip_code}</div>
                </td>
                <td className="px-4 py-3">
                  {l.routed_to && l.routed_to.length > 0 ? (
                    <span className="px-2 py-1 text-[10px] font-mono-ll tracking-widest uppercase bg-[#1A365D] text-white">{l.routed_to.length} MFR</span>
                  ) : (
                    <span className="text-xs text-slate-400 font-mono-ll tracking-widest uppercase">No match</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} className="ll-select text-xs py-1" data-testid={`lead-status-${l.id}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drawer */}
      {selected && <LeadDrawer lead={selected} onClose={() => setSelected(null)} onStatusChange={(s) => updateStatus(selected.id, s)} onReroute={() => rerouteLead(selected.id)}/>}
    </div>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className={`p-4 border-2 ${accent ? "border-[#C62828] bg-[#C62828] text-white" : "border-[#1A365D] bg-white"}`}>
      <div className={`font-mono-ll text-[10px] tracking-widest uppercase ${accent ? "text-white/80" : "text-slate-500"}`}>{label}</div>
      <div className={`font-heading text-3xl font-black mt-1 ${accent ? "text-white" : "text-[#1A365D]"}`}>{value}</div>
    </div>
  );
}

function LeadDrawer({ lead, onClose, onStatusChange, onReroute }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose} data-testid="lead-drawer">
      <div className="bg-white w-full max-w-xl h-full border-l-2 border-[#1A365D] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="btn-ghost float-right" data-testid="close-drawer-btn">Close ×</button>
        <div className="label-tag">Lead Detail</div>
        <h2 className="font-heading text-3xl md:text-4xl font-black uppercase mt-1 text-[#1A365D] leading-none">{lead.product_type}</h2>
        <div className="mt-3 flex items-center gap-2">
          <span className={`px-3 py-1 text-[10px] font-mono-ll tracking-widest uppercase ${STATUS_COLORS[lead.status]}`}>{lead.status}</span>
          <span className="font-mono-ll text-xs text-slate-500">ID {lead.id.slice(0,8)}</span>
        </div>

        <div className="mt-6 space-y-3 text-sm">
          <Row k="Contractor" v={lead.contractor_name}/>
          <Row k="Company" v={lead.company || "—"}/>
          <Row k="Email" v={lead.email}/>
          <Row k="Phone" v={lead.phone}/>
          <Row k="Location" v={[lead.city, lead.state].filter(Boolean).join(", ") || "—"}/>
          <Row k="ZIP" v={lead.zip_code}/>
          <Row k="Quantity" v={lead.quantity}/>
          <Row k="Timeline" v={lead.timeline || "—"}/>
          <Row k="Budget" v={lead.budget || "—"}/>
          <Row k="Specifications" v={lead.specifications} pre/>
          {lead.notes && <Row k="Notes" v={lead.notes} pre/>}
          <Row k="Submitted" v={new Date(lead.created_at).toLocaleString()}/>
        </div>

        <RoutingBlock lead={lead} onReroute={onReroute}/>

        <div className="mt-8">
          <div className="label-tag mb-2">Update Status</div>
          <div className="flex gap-2 flex-wrap">
            {STATUSES.map((s) => (
              <button key={s} onClick={() => onStatusChange(s)} data-testid={`drawer-set-${s}`}
                className={`px-3 py-2 text-xs font-mono-ll tracking-widest uppercase border-2 ${lead.status === s ? STATUS_COLORS[s] + " border-transparent" : "border-[#1A365D] text-[#1A365D] hover:bg-[#1A365D] hover:text-white"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function RoutingBlock({ lead, onReroute }) {
  const routed = lead.routed_to || [];
  return (
    <div className="mt-8 border-2 border-[#1A365D] p-4" data-testid="lead-routing-block">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-tag">Auto-Routing</div>
        <button onClick={onReroute} className="text-xs font-mono-ll tracking-widest uppercase text-[#C62828] hover:underline" data-testid="reroute-btn">
          ↻ Re-route now
        </button>
      </div>
      {lead.routed_at ? (
        <div className="mt-2 font-mono-ll text-[10px] tracking-widest text-slate-500 uppercase">
          Last routed {new Date(lead.routed_at).toLocaleString()}
        </div>
      ) : (
        <div className="mt-2 font-mono-ll text-[10px] tracking-widest text-slate-500 uppercase">Not routed yet</div>
      )}

      {routed.length === 0 ? (
        <div className="mt-3 text-sm text-slate-600">
          No manufacturers matched. Add dealers in this lead's area to enable routing.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {routed.map((r) => (
            <div key={r.manufacturer_id} className="flex items-start justify-between gap-3 text-sm border-t border-slate-200 pt-2 first:border-0 first:pt-0" data-testid={`routed-mfr-${r.manufacturer_id}`}>
              <div>
                <div className="font-bold text-[#1A365D]">{r.manufacturer_name || r.manufacturer_email}</div>
                <div className="font-mono-ll text-[10px] tracking-widest text-slate-500 uppercase">
                  {r.matched_dealers_count} dealer{r.matched_dealers_count === 1 ? "" : "s"} · {r.manufacturer_email}
                </div>
              </div>
              <span className={`px-2 py-1 text-[10px] font-mono-ll tracking-widest uppercase ${r.emailed ? "bg-green-700 text-white" : "bg-yellow-500 text-white"}`}>
                {r.emailed ? "Emailed" : "Email blocked"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ k, v, pre }) {
  return (
    <div className="border-2 border-[#1A365D] p-3">
      <div className="label-tag mb-1">{k}</div>
      <div className={`text-[#1A365D] ${pre ? "whitespace-pre-wrap" : ""}`}>{v}</div>
    </div>
  );
}
